// Loom's public pages: privacy, imprint, support. Reachable signed out — the
// routes (`router.tsx`) have no guard and the page asks the server nothing.
//
// The page's language is `?lang=en|de` when the address carries one (so a link
// handed to a reviewer opens in the language it names), else the interface
// language. Switching here changes only this page's address, never the stored
// interface language.

import { PUBLIC_PRODUCT_NAME } from "@loom/shared/brand";
import { Link } from "@tanstack/react-router";
import type { JSX } from "react";

import { translate, useLocale } from "../i18n/index.js";
import { LOGO_SRC } from "../ui/logo";

import { PRIVACY_UPDATED } from "./facts";
import { IMPRINT, SUPPORT } from "./imprint";
import { Inline } from "./inline";
import { LEGAL_PATHS, LegalLinks, type LegalKind } from "./LegalLinks";
import { PRIVACY } from "./privacy";
import type { LegalBlock, LegalDoc, LegalLang } from "./types";

const DOCS: Readonly<Record<LegalKind, Readonly<Record<LegalLang, LegalDoc>>>> = {
  privacy: PRIVACY,
  imprint: IMPRINT,
  support: SUPPORT,
};

const LANGS: readonly LegalLang[] = ["en", "de"];

const isLegalLang = (v: unknown): v is LegalLang => v === "en" || v === "de";

/** `?lang=` as the routes validate it: a known language, or nothing. The key is
 *  always present: the root route keeps the raw search, and only an explicit
 *  `undefined` overrides a raw `?lang=fr` there. */
export const legalSearch = (search: Record<string, unknown>): { lang: LegalLang | undefined } => ({
  lang: isLegalLang(search.lang) ? search.lang : undefined,
});

function Block({ block }: { readonly block: LegalBlock }): JSX.Element {
  switch (block.kind) {
    case "p":
      return (
        <p className="mb-4 leading-relaxed text-ink-2">
          <Inline text={block.text} />
        </p>
      );
    case "list":
      return (
        <ul className="mb-4 list-disc space-y-2 pl-5 leading-relaxed text-ink-2">
          {block.items.map((item, i) => (
            <li key={i}>
              <Inline text={item} />
            </li>
          ))}
        </ul>
      );
    case "rows":
      return (
        <dl className="mb-4 divide-y divide-line-soft rounded-lg border border-line bg-surface">
          {block.rows.map(([label, value]) => (
            <div key={label} className="grid gap-1 px-4 py-3 sm:grid-cols-[14rem_1fr] sm:gap-4">
              <dt className="text-sm text-ink-muted">{label}</dt>
              <dd className="min-w-0 break-words text-ink-2">
                <Inline text={value} />
              </dd>
            </div>
          ))}
        </dl>
      );
  }
}

export function LegalPage({
  kind,
  lang: pinned,
}: {
  readonly kind: LegalKind;
  /** From `?lang=`; the interface language when absent. */
  readonly lang?: LegalLang;
}): JSX.Element {
  const { locale } = useLocale();
  const lang: LegalLang = isLegalLang(pinned) ? pinned : locale;
  const doc = DOCS[kind][lang];
  const long = doc.sections.length > 4;

  return (
    <div className="min-h-full bg-canvas text-ink">
      <div className="mx-auto w-full max-w-3xl px-4 py-8 sm:px-6 sm:py-12">
        <header className="mb-10 flex items-center justify-between gap-4">
          <a
            href="/"
            className="flex items-center gap-2 font-semibold text-ink"
            aria-label={translate(lang, "legal.home")}
          >
            <img src={LOGO_SRC} alt="" aria-hidden className="h-8 w-8 rounded-md" />
            <span>{PUBLIC_PRODUCT_NAME}</span>
          </a>
          <nav
            aria-label={translate(lang, "legal.language")}
            className="flex items-center gap-1 text-sm"
          >
            {LANGS.map((l) => (
              <Link
                key={l}
                to={LEGAL_PATHS[kind]}
                search={{ lang: l }}
                hrefLang={l}
                className={
                  l === lang
                    ? "rounded-md bg-surface-3 px-2 py-1 font-medium text-ink"
                    : "rounded-md px-2 py-1 text-ink-muted hover:bg-surface-2 hover:text-ink"
                }
              >
                {l.toUpperCase()}
              </Link>
            ))}
          </nav>
        </header>

        <article lang={lang}>
          <h1 className="text-3xl font-semibold tracking-tight text-ink">{doc.title}</h1>
          {kind === "privacy" ? (
            <p className="mt-2 text-sm text-ink-muted">
              {translate(lang, "legal.updated", { date: PRIVACY_UPDATED[lang] })}
            </p>
          ) : null}
          <p className="mt-4 text-lg leading-relaxed text-ink-2">
            <Inline text={doc.intro} />
          </p>

          {long ? (
            <nav
              aria-label={translate(lang, "legal.contents")}
              className="mt-8 rounded-lg border border-line bg-surface px-4 py-3"
            >
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-ink-subtle">
                {translate(lang, "legal.contents")}
              </p>
              <ul className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
                {doc.sections.map((s) => (
                  <li key={s.id}>
                    <a href={`#${s.id}`} className="text-primary-fg hover:underline">
                      {s.heading}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          ) : null}

          {doc.sections.map((section) => (
            <section
              key={section.id}
              id={section.id}
              aria-labelledby={`${section.id}-h`}
              className="mt-10 scroll-mt-6"
            >
              <h2 id={`${section.id}-h`} className="mb-4 text-xl font-semibold text-ink">
                {section.heading}
              </h2>
              {section.blocks.map((block, i) => (
                <Block key={i} block={block} />
              ))}
            </section>
          ))}
        </article>

        <footer className="mt-12 border-t border-line pt-6">
          <LegalLinks lang={lang} />
        </footer>
      </div>
    </div>
  );
}
